/**
 * The left half of the queue page: one result as the agent saw and answered
 * it — the input, the agent's output and, when the dataset has one, the
 * expected output, full width and read as fields (`answer-view.tsx`) — with
 * the experiment it came from and a link to its trace.
 */

import { ExternalLink } from "lucide-react";
import { Box, DetailSection } from "@/components/ui/box";
import { AnswerView } from "./answer-view";
import type { QueueItem } from "./types";

/** Text (a report) as prose; anything structured as fields. */
function Value({ value, label }: { value: unknown; label: string }) {
  if (typeof value === "string") {
    return (
      <p
        aria-label={label}
        className="m-0 rounded-lg border border-line-soft bg-well px-3.5 py-2.5 text-[13px] leading-relaxed
                   whitespace-pre-wrap text-code"
      >
        {value}
      </p>
    );
  }
  return <AnswerView value={value} label={label} />;
}

export function ItemView({ item }: { item: QueueItem }) {
  const inputs = Object.entries(item.input);
  return (
    <Box label="Item">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-line-soft px-4 py-3">
        <h2 className="font-mono text-sm font-semibold">Item {item.position + 1}</h2>
        <span className="text-xs text-muted">from {item.experiment_name}</span>
        <span className="grow" />
        {item.trace_url && (
          <a
            href={item.trace_url}
            target="_blank"
            rel="noreferrer"
            className="link inline-flex items-center gap-1 text-[13px]"
          >
            Open trace
            <ExternalLink size={12} strokeWidth={2} aria-hidden />
          </a>
        )}
      </div>
      <div className="flex flex-col gap-4 p-4">
        <DetailSection title="Input">
          {inputs.length ? (
            inputs.map(([k, v]) => (
              <div key={k} className="flex flex-col gap-1">
                <span className="font-mono text-xs text-muted">{k}</span>
                <Value value={v} label={k} />
              </div>
            ))
          ) : (
            <span className="text-[13px] text-muted">No input recorded.</span>
          )}
        </DetailSection>
        {/* Full width, one under the other: answers are long, and half a column cut them up. */}
        <DetailSection title="Agent output">
          <AnswerView value={item.output} label="Agent output" />
        </DetailSection>
        {item.expected_output != null && (
          <DetailSection title="Expected output">
            <AnswerView value={item.expected_output} label="Expected output" />
          </DetailSection>
        )}
      </div>
    </Box>
  );
}
